import { freeSpacePathLossDb } from './index.js';
import { rainSpecificAttenuation, type Polarization } from './rain.js';

export interface McsEntry {
  name: string;
  modulation: string;
  sensitivityDbm: number;
  minimumSnrDb: number;
  phyRateMbps: number;
  usableThroughputMbps: number;
}

export interface RadioPerformanceProfile {
  source: string;
  channelWidthMHz: number;
  entries: McsEntry[];
}

export interface LinkBudgetInput {
  frequencyGHz: number;
  distanceKm: number;
  channelWidthMHz: number;
  txPowerDbm: number;
  txAntennaGainDbi: number;
  rxAntennaGainDbi: number;
  txCableLossDb: number;
  rxCableLossDb: number;
  additionalLossDb: number;
  receiverSensitivityDbm?: number;
  noiseFigureDb?: number;
  ambientNoiseFloorDbm?: number;
  predictedInterferenceDbm?: number;
  rainRateMmPerHour?: number;
  polarization?: Polarization;
  pathElevationDeg?: number;
  rainEffectivePathFactor?: number;
  performanceProfile?: RadioPerformanceProfile;
  maximumThroughputMbps?: number;
}

export interface LinkBudgetResult {
  fsplDb: number;
  eirpDbm: number;
  rainLossDb: number;
  totalPathLossDb: number;
  rxSignalDbm: number;
  thermalNoiseFloorDbm: number;
  effectiveNoiseFloorDbm: number;
  snrDb: number;
  receiverSensitivityDbm: number;
  fadeMarginDb: number;
  modulation: string;
  phyRateMbps: number;
  usableThroughputMbps: number;
  throughputEstimated: boolean;
  performanceSource: string;
}

function combinePowersDbm(values: number[]): number {
  const linear = values.filter(Number.isFinite).reduce((sum, value) => sum + 10 ** (value / 10), 0);
  return linear > 0 ? 10 * Math.log10(linear) : Number.NEGATIVE_INFINITY;
}

function genericPerformance(snrDb: number, bandwidthMHz: number): { modulation: string; phyRateMbps: number; usableThroughputMbps: number; sensitivityDbm: number } {
  const snrLinear = 10 ** (snrDb / 10);
  const maximumSpectralEfficiency = 10;
  const practicalSpectralEfficiency = Math.min(maximumSpectralEfficiency, Math.max(0, Math.log2(1 + snrLinear) * 0.55));
  const phyRateMbps = bandwidthMHz * practicalSpectralEfficiency;
  const modulation = snrDb >= 32 ? '1024-QAM (оценка)' : snrDb >= 25 ? '256-QAM (оценка)' : snrDb >= 18 ? '64-QAM (оценка)' : snrDb >= 11 ? '16-QAM (оценка)' : snrDb >= 5 ? 'QPSK (оценка)' : 'BPSK / нет устойчивого MCS';
  return { modulation, phyRateMbps, usableThroughputMbps: phyRateMbps * 0.65, sensitivityDbm: -82 };
}

export function calculateLinkBudget(input: LinkBudgetInput): LinkBudgetResult {
  if (input.channelWidthMHz <= 0) throw new RangeError('Channel width must be positive');
  if (input.maximumThroughputMbps !== undefined && (!Number.isFinite(input.maximumThroughputMbps) || input.maximumThroughputMbps <= 0)) {
    throw new RangeError('Maximum throughput must be positive');
  }
  const fsplDb = freeSpacePathLossDb(input.frequencyGHz, input.distanceKm);
  const eirpDbm = input.txPowerDbm + input.txAntennaGainDbi - input.txCableLossDb;
  const rain = rainSpecificAttenuation({
    frequencyGHz: input.frequencyGHz,
    rainRateMmPerHour: input.rainRateMmPerHour ?? 0,
    ...(input.polarization ? { polarization: input.polarization } : {}),
    ...(input.pathElevationDeg === undefined ? {} : { pathElevationDeg: input.pathElevationDeg }),
  });
  const rainLossDb = rain.specificAttenuationDbPerKm * input.distanceKm * (input.rainEffectivePathFactor ?? 1);
  const totalPathLossDb = fsplDb + input.additionalLossDb + rainLossDb;
  const rxSignalDbm = input.txPowerDbm + input.txAntennaGainDbi + input.rxAntennaGainDbi - input.txCableLossDb - input.rxCableLossDb - totalPathLossDb;
  const thermalNoiseFloorDbm = -174 + 10 * Math.log10(input.channelWidthMHz * 1_000_000) + (input.noiseFigureDb ?? 6);
  const baseNoiseDbm = Math.max(thermalNoiseFloorDbm, input.ambientNoiseFloorDbm ?? -95);
  const effectiveNoiseFloorDbm = input.predictedInterferenceDbm === undefined ? baseNoiseDbm : combinePowersDbm([baseNoiseDbm, input.predictedInterferenceDbm]);
  const snrDb = rxSignalDbm - effectiveNoiseFloorDbm;
  const matchingEntry = input.performanceProfile?.entries
    .filter((entry) => input.performanceProfile?.channelWidthMHz === input.channelWidthMHz && rxSignalDbm >= entry.sensitivityDbm && snrDb >= entry.minimumSnrDb)
    .sort((a, b) => b.phyRateMbps - a.phyRateMbps)[0];
  const generic = genericPerformance(snrDb, input.channelWidthMHz);
  const receiverSensitivityDbm = input.receiverSensitivityDbm ?? matchingEntry?.sensitivityDbm ?? generic.sensitivityDbm;
  const usableThroughputMbps = Math.min(
    matchingEntry?.usableThroughputMbps ?? generic.usableThroughputMbps,
    input.maximumThroughputMbps ?? Number.POSITIVE_INFINITY,
  );
  return {
    fsplDb,
    eirpDbm,
    rainLossDb,
    totalPathLossDb,
    rxSignalDbm,
    thermalNoiseFloorDbm,
    effectiveNoiseFloorDbm,
    snrDb,
    receiverSensitivityDbm,
    fadeMarginDb: rxSignalDbm - receiverSensitivityDbm,
    modulation: matchingEntry?.modulation ?? generic.modulation,
    phyRateMbps: matchingEntry?.phyRateMbps ?? generic.phyRateMbps,
    usableThroughputMbps,
    throughputEstimated: matchingEntry === undefined,
    performanceSource: matchingEntry ? input.performanceProfile?.source ?? 'device profile' : 'Generic Shannon-based estimate',
  };
}
